import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { AppShell } from "@/components/dash/AppShell";
import { StatusBadge, fmtDate } from "@/components/admin/StatusBadge";
import { CompBlock, TrialBlock, EmailBlock, SuspendBlock, PreviewLimitBlock } from "@/components/admin/AccountActions";
import { AuditList } from "@/components/admin/AuditList";
import { AdminNotes } from "@/components/admin/AdminNotes";
import { ManualLinks } from "@/components/admin/ManualLinks";

const LABELS = { comp: "Comp saved", uncomp: "Comp removed", "extend-trial": "Trial extended", "verify-email": "Email marked as verified",
  suspend: "Account suspended", unsuspend: "Account reactivated", "resend-verification": "Verification email sent", "preview-limit": "Preview limit saved",
  notes: "Note added", note_delete: "Note deleted" };

const REMINDERS = [["3d", "3 days before trial ends"], ["0d", "Day the trial ends"], ["winback", "Win-back (7 days after trial)"]];
const sentAt = (iso) => new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });

const TrialReminders = ({ sent }) => (
  <div className="rounded-2xl border bg-card p-5 shadow-soft" data-testid="admin-trial-reminders">
    <h2 className="text-base font-semibold text-foreground">Trial emails</h2>
    <ul className="mt-3 space-y-2">
      {REMINDERS.map(([k, label]) => (
        <li key={k} className="flex items-center justify-between gap-3 text-sm" data-testid={`admin-trial-reminder-${k}`}>
          <span className="text-foreground">{label}</span>
          <span className={sent[k] ? "font-medium text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}>{sent[k] ? `Sent ${sentAt(sent[k])}` : "Not sent"}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Info = ({ a }) => {
  const rows = [["Email", a.email], ["Phone", a.phone || "—"], ["Location", a.city ? `${a.city}, ${a.state || ""}` : "—"], ["Service area", a.service_area || "—"],
    ["Public link", `/q/${a.slug}`], ["Signed up", fmtDate(a.created_at)], ["Last login", fmtDate(a.last_login_at)], ["Trial ends", fmtDate(a.trial_ends_at)],
    ["Leads", a.leads], ["Email verified", a.email_verified ? "Yes" : "No"], ["Stripe status", a.stripe_status || "No subscription"],
    ["Public link live", a.is_active ? "Yes" : "No"]];
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 rounded-2xl border bg-card p-5 shadow-soft sm:grid-cols-2" data-testid="admin-account-info">
      {rows.map(([k, v]) => (
        <div key={k} className="min-w-0"><dt className="text-xs text-muted-foreground">{k}</dt><dd className="truncate text-sm font-medium text-foreground">{v}</dd></div>
      ))}
      <div className="sm:col-span-2"><dt className="text-xs text-muted-foreground">Services ({a.services.length})</dt>
        <dd className="mt-1.5 flex flex-wrap gap-1.5" data-testid="admin-account-services">
          {a.services.map((s) => <span key={s} className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-foreground">{s}</span>)}
        </dd>
      </div>
    </dl>
  );
};

export default function AdminAccount() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/admin/accounts/${id}`).then(({ data }) => setA(data)).catch((e) => toast.error(errorMessage(e))), [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (action, body, method = "post", label = LABELS[action]) => {
    setBusy(true);
    try { const { data } = await api[method](`/admin/accounts/${id}/${action}`, body); setA(data); toast.success(label); return true; }
    catch (e) { toast.error(errorMessage(e)); return false; }
    finally { setBusy(false); }
  };

  return (
    <AppShell allowInactive>
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground" data-testid="admin-back-link"><ArrowLeft className="h-4 w-4" />All accounts</Link>
      {!a ? <div className="flex h-60 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div> : (
        <div className="mt-4 space-y-5" data-testid="admin-account-page">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-foreground" data-testid="admin-account-name">{a.company_name}</h1>
            <StatusBadge status={a.status} suspended={a.suspended} />
            {a.is_test && <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">Test account</span>}
          </div>
          {a.double_billing && (
            <p className="flex items-start gap-2 rounded-xl border border-yellow-300 bg-yellow-100 p-3 text-sm text-yellow-900" data-testid="admin-double-billing-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />This account is comped but still has an active Stripe subscription, so it is being charged. Cancel it in the Stripe dashboard if it should be free.
            </p>
          )}
          <Info a={a} />
          <section className="space-y-3">
            <h2 className="text-base font-semibold text-foreground">Actions</h2>
            <div className="grid gap-3 lg:grid-cols-2">
              <CompBlock a={a} run={run} busy={busy} />
              <TrialBlock a={a} run={run} busy={busy} />
              <EmailBlock a={a} run={run} busy={busy} />
              <SuspendBlock a={a} run={run} busy={busy} />
              <PreviewLimitBlock key={a.preview_limit ?? "default"} a={a} run={run} busy={busy} />
              {(!a.email_verified || !a.is_self) && <ManualLinks a={a} onDone={load} />}
            </div>
          </section>
          <TrialReminders sent={a.trial_reminders || {}} />
          <AdminNotes notes={a.notes} busy={busy} onAdd={(text) => run("notes", { text })}
            onDelete={(noteId) => run(`notes/${noteId}`, undefined, "delete", LABELS.note_delete)} />
          <AuditList items={a.audit} />
        </div>
      )}
    </AppShell>
  );
}
